import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { Accessibility, Link2, Radio, Shield, Utensils } from "lucide-react"
import type { ReactNode } from "react"
import { resolveEventId } from "@/core/active-event"
import { formsQueryOptions } from "@/core/forms"
import { eventDietaryReportQueryOptions } from "@/core/people"
import { commsReportQueryOptions } from "@/core/comms-report"
import { DietaryReport } from "@/components/reports/DietaryReport"
import { CommsReport } from "@/components/reports/CommsReport"
import { SystemFormReport } from "@/components/reports/SystemFormReport"
import { SharedAccessPanel } from "@/components/reports/SharedAccessPanel"
import { ACCOMMODATIONS_REPORT, EMERGENCY_REPORT } from "@/components/reports/report-configs"
import { cn } from "@/core/utils"

// Guest reports under one roof, laid out like Event Settings: a left sub-nav
// (icon rail → labels on wide canvases) drives the report shown on the right.
// The active section lives in the URL (?tab=) so it's linkable; the old
// standalone routes redirect here. Each report card carries a single Export ▾
// dropdown (PDF / Word / CSV) for that report.
type ReportTab = "dietary" | "accommodations" | "emergency" | "comms" | "shared"

const NAV: Array<{ id: ReportTab; label: string; icon: ReactNode }> = [
  { id: "dietary", label: "Dietary", icon: <Utensils /> },
  { id: "accommodations", label: "Accommodations", icon: <Accessibility /> },
  { id: "emergency", label: "Emergency", icon: <Shield /> },
  { id: "comms", label: "Comms reach", icon: <Radio /> },
]

export const Route = createFileRoute("/$orgSlug/$eventSlug/reports")({
  validateSearch: (s: Record<string, unknown>): { tab: ReportTab } => ({
    tab:
      s.tab === "accommodations" ||
      s.tab === "emergency" ||
      s.tab === "comms" ||
      s.tab === "shared"
        ? s.tab
        : "dietary",
  }),
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient.ensureQueryData(formsQueryOptions(eventId)).catch(() => undefined)
      void context.queryClient
        .ensureQueryData(eventDietaryReportQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(commsReportQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: ReportsView,
})

function ReportsView() {
  const { tab } = Route.useSearch()
  const params = useParams({ from: "/$orgSlug/$eventSlug/reports" })
  const navigate = useNavigate()
  const setTab = (t: ReportTab) =>
    void navigate({
      to: "/$orgSlug/$eventSlug/reports",
      params,
      search: { tab: t },
      replace: true,
    })

  return (
    <div className="relative flex h-full min-h-0">
      {/* sub-nav — collapses to an icon rail on narrow canvases (mirrors Settings) */}
      <nav className="flex w-[58px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-plum-200/50 p-2 @[560px]:w-[200px] @[560px]:p-3 dark:border-white/10">
        <p className="mb-1 hidden px-2 text-[11px] font-semibold uppercase tracking-wide text-plum-700/40 @[560px]:block dark:text-plum-200/30">
          Reports
        </p>
        {NAV.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            title={item.label}
            aria-current={tab === item.id ? "page" : undefined}
            className={cn(
              "group flex items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] font-medium text-plum-800/80 transition hover:bg-plum-100/60 @[560px]:px-3 dark:text-plum-200/70 dark:hover:bg-white/5",
              tab === item.id &&
                "bg-[var(--accent)]/12 font-semibold text-[var(--accent)] dark:text-plum-100",
            )}
          >
            <span className="grid h-5 w-5 shrink-0 place-items-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
              {item.icon}
            </span>
            <span className="hidden truncate @[560px]:block">{item.label}</span>
          </button>
        ))}

        {/* "Shared access" is pinned to the bottom, separated from the report
            list — it's a cross-cutting management view, not another report. */}
        <button
          type="button"
          onClick={() => setTab("shared")}
          title="Shared access"
          aria-current={tab === "shared" ? "page" : undefined}
          className={cn(
            "group mt-auto flex items-center gap-2.5 rounded-lg border-t border-plum-200/50 px-2 pt-3 pb-2 text-[13px] font-medium text-plum-800/80 transition hover:bg-plum-100/60 @[560px]:px-3 dark:border-white/10 dark:text-plum-200/70 dark:hover:bg-white/5",
            tab === "shared" &&
              "bg-[var(--accent)]/12 font-semibold text-[var(--accent)] dark:text-plum-100",
          )}
        >
          <span className="grid h-5 w-5 shrink-0 place-items-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
            <Link2 />
          </span>
          <span className="hidden truncate @[560px]:block">Shared access</span>
        </button>
      </nav>

      {/* Each report card owns a single Export ▾ dropdown (PDF / Word / CSV). */}
      <div className="relative flex min-w-0 flex-1">
        {tab === "dietary" && <DietaryReport />}
        {tab === "accommodations" && <SystemFormReport {...ACCOMMODATIONS_REPORT} />}
        {tab === "emergency" && <SystemFormReport {...EMERGENCY_REPORT} />}
        {tab === "comms" && <CommsReport />}
        {tab === "shared" && <SharedAccessPanel />}
      </div>
    </div>
  )
}
